import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';

import {
  ConfirmationDialogActionCancelButton,
  ConfirmationDialogActionProceedButton,
  ConfirmationDialogActions,
  ConfirmationDialogContainer,
  ConfirmationDialogHeader,
} from '@/components/dialogs';
import { formatNumberByThousands } from '@/shared/helpers/format.helpers';
import { useSpecification } from '@/shared/hooks/useSpecification';
import { useSpecificationContractDocument } from '@/shared/hooks/useSpecificationContractDocument';
import { DorimPromoPaths, type SpecificationDetailPathParams } from '@/shared/lib/react-router';

import { useSpecificationContextWindowsVisibility, useSpecificationStoreActions } from '../../../../lib';
import { useConfirmReceivingBonus } from './Actions/lib';

export const ReceivePaymentDialog = () => {
  const params = useParams<SpecificationDetailPathParams>();
  const specificationId = Number(params.specificationId);
  const { data } = useSpecification({ specificationId });
  const { t } = useTranslation('specifications-detail-page');

  const { agreement } = data || {};

  const { isSigned: isSpecificationContractSigned } = useSpecificationContractDocument({ specificationId });
  const { mutate: confirmReceivingBonus, isLoading } = useConfirmReceivingBonus();

  const { isBonusConfirmationDialogVisible } = useSpecificationContextWindowsVisibility();
  const { closeBonusConfirmationDialog } = useSpecificationStoreActions();

  const navigate = useNavigate();

  const handleProceed = () => {
    if (!params.specificationId) {
      return;
    }

    if (isSpecificationContractSigned) {
      confirmReceivingBonus(specificationId, { onSuccess: closeBonusConfirmationDialog });
      return;
    }

    navigate(
      DorimPromoPaths.generateSpecificationDetailContractDocumentPath({
        specificationId: params.specificationId,
      }),
    );
  };

  return (
    <ConfirmationDialogContainer
      open={isBonusConfirmationDialogVisible}
      subject="confirm"
      onClose={closeBonusConfirmationDialog}
    >
      <ConfirmationDialogHeader>{t('dialogs.receive-payment-dialog.title')}</ConfirmationDialogHeader>
      <Stack spacing={3}>
        {agreement && (
          <Typography variant="Body/medium" color="neutral.700">
            {t('dialogs.receive-payment-dialog.description.agree')}{' '}
            <Typography component="span" variant="inherit" color="text.primary">
              <Typography component="span" variant="inherit" fontWeight={600}>
                {formatNumberByThousands(agreement.cashback_sum)}
              </Typography>{' '}
              <Typography component="span" variant="inherit">
                UZS
              </Typography>
            </Typography>{' '}
            {t('dialogs.receive-payment-dialog.description.plan-completing')}
          </Typography>
        )}
        <Typography variant="Body/medium" color="neutral.700">
          {t('dialogs.receive-payment-dialog.payment-info')}
        </Typography>
      </Stack>

      <ConfirmationDialogActions>
        <ConfirmationDialogActionCancelButton onClick={closeBonusConfirmationDialog} />
        <ConfirmationDialogActionProceedButton autoFocus loading={isLoading} onClick={handleProceed}>
          {t('dialogs.receive-payment-dialog.actions.navigate-to-contract-document')}
        </ConfirmationDialogActionProceedButton>
      </ConfirmationDialogActions>
    </ConfirmationDialogContainer>
  );
};
